import { Component, EventEmitter, Input, OnInit, Output, WritableSignal, computed, inject, model, output, signal } from '@angular/core';
import { AccountService, PaymentMethod } from '../../../shared/account.service';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ArrayOptionsItem, PaymentDataPayload } from '../../../shared/types/account.types';
import { PaymentMethodService } from '../../../settings/billing/payment-method/payment-method.service';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { provideNativeDateAdapter, DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { CUSTOM_DATE_FORMATS } from '../../../shared/constants/date-formtats';
import { MatStep, MatStepper, MatStepperModule } from '@angular/material/stepper';
import { finalize, tap } from 'rxjs';
import { CreditCardService } from '../../../shared/credit-card.service';
import { OnlyNumberDirective } from '../../../shared/directives/only-number.directive';
import { LocalStorageService } from '../../../shared/local-storage.service';
import { FIREBASE_COLLECTIONS, LOCALSTORAGE_KEYS } from '../../../shared/constants/databases';
import { EmptyMessageComponent } from '../../../shared/empty-message/empty-message.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { RouterModule } from '@angular/router';
import { ANetPayload, ANetResponse } from '../../../shared/purchase/authorize/authorize.types';
import { AuthorizeService } from '../../../shared/purchase/authorize/authorize.service';
import { FirebaseCrudService } from '../../../shared/firebase-crud.service';
import { Session } from '../../../shared/types/session.type';
import { MaskDateDirective } from '../../../shared/directives/maskDate.directive';
import { PaymentMethodSignupService } from './payment-method-signup.service';

@Component({
  selector: 'app-payment-method-signup',
  providers: [provideNativeDateAdapter(),
  { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
  { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS.MONTH_AND_YEAR },
    PaymentMethodService
  ],
  imports: [
    FormsModule,
    CommonModule,
    MatFormFieldModule,
    MatStepperModule,
    MatCheckboxModule,
    MatInputModule,
    MatDatepickerModule,
    MatIconModule,
    ReactiveFormsModule,
    MatSlideToggleModule,
    MatSelectModule,
    OnlyNumberDirective,
    MaskDateDirective,
    EmptyMessageComponent,
    RouterModule
  ],
  templateUrl: './payment-method-signup.component.html',
  styleUrl: './payment-method-signup.component.scss'
})
export class PaymentMethodSignupComponent implements OnInit {

  private paymentMethodService = inject(PaymentMethodService);
  accountService = inject(AccountService);
  private creditCardService = inject(CreditCardService);
  private localStorageService = inject(LocalStorageService);
  private firebaseCrudService = inject(FirebaseCrudService);
  #authorizeService = inject(AuthorizeService);
  #paymentMethodSignupService = inject(PaymentMethodSignupService);

  @Output() formValidityChanged = new EventEmitter<boolean>();
  @Output() onNextStep: EventEmitter<any> = new EventEmitter<any>()

  @Input() stepper!: MatStepper;
  @Input() firstStep!: MatStep;

  out_paymentMethod = output<PaymentMethod>();
  out_isValidFormPaymentMethod = output<boolean>();
  out_continue = output<void>();
  isWaitingResponse: any = signal(false)

  paymentMethod: WritableSignal<PaymentMethod> = signal({
    cardCode: '',
    firstName: '',
    lastName: '',
    cardNumber: '',
    email: '',
    expirationDate: '',
  })

  isValidFormPaymentMethod = computed(() => this.validatePaymentMethodForm());
  paymentMethodValidations = new Map<string, WritableSignal<{ valid: boolean, error: string, validations: string[], touched: boolean }>>();
  isCheckedBillingAddress: WritableSignal<boolean> = signal(false);
  billingAddress: any = {};

  date = new FormControl();

  states: WritableSignal<ArrayOptionsItem[]> = signal([]);

  creditCardType: WritableSignal<any> = signal(null);
  creditCardSecurityCodeMaxLen = computed(() => {
    const cctype = this.creditCardType();
    let maxlen = 4;
    if (cctype) maxlen = cctype.securityCodeLen || maxlen;

    return maxlen;
  });

  disclaimer = "We will not charge your credit card until your first delivery. We process billing on Sunday after 5pm.";

  isCheckedTermsAndConditions = model(false);

  isCheckDone = signal(false);

  ngOnInit(): void {
    this.setUpPaymentMethodValidations();
  }

  private validatePaymentMethodForm() {
    const formFilled = !!(
      this.paymentMethod()?.firstName &&
      this.paymentMethod()?.lastName &&
      this.paymentMethod()?.cardCode &&
      this.paymentMethod()?.cardNumber &&
      this.paymentMethod()?.expirationDate &&
      (
        !this.isCheckedBillingAddress() ||
        (
          this.isCheckedBillingAddress() &&
          this.paymentMethod()?.address &&
          this.paymentMethod()?.city &&
          this.paymentMethod()?.state &&
          this.paymentMethod()?.zipCode
        )
      )
    );

    if (!formFilled) {
      this.out_isValidFormPaymentMethod.emit(false);
      return false;
    }

    let valid = true;
    const keys: string[] = Array.from(this.paymentMethodValidations.keys());
    for (const key of keys) {
      if (!this.paymentMethodValidations.get(key)?.()?.valid) {
        valid = false;
        break;
      }
    }
    this.out_isValidFormPaymentMethod.emit(valid);
    this.formValidityChanged.emit(valid);
    return valid;
  }

  formatCreditCardNumber(event: any) {
    let value = event.target.value;

    if (!value) return;

    let cleaned = value.replace(/\D/g, '');

    cleaned = cleaned.substring(0, 16);

    this.creditCardType.set(this.creditCardService.typeCard(cleaned));

    let formatted = cleaned.replace(/(.{4})/g, '$1 ').trim();

    event.target.value = formatted;
    this.paymentMethod().cardNumber = formatted;
    this.updatePaymentMethod({ cardNumber: cleaned });
  }

  private setUpPaymentMethodValidations() {
    const defaultValue = { valid: true, error: '', validations: ['required'], touched: false };
    this.paymentMethodValidations.set('firstName', signal(defaultValue));
    this.paymentMethodValidations.set('lastName', signal(defaultValue));
    this.paymentMethodValidations.set('expirationDate', signal(defaultValue));
    this.paymentMethodValidations.set('cardCode', signal({ ...defaultValue, validations: [...defaultValue.validations, 'number', 'exactLength'] }));
    this.paymentMethodValidations.set('cardNumber', signal(defaultValue));
    this.tearDownPaymentMethodValidations([]);
  }

  private tearDownPaymentMethodValidations(validations: string[]) {
    const resetValue = { valid: true, error: '', validations, touched: false };
    this.paymentMethodValidations.set('address', signal(resetValue));
    this.paymentMethodValidations.set('city', signal(resetValue));
    this.paymentMethodValidations.set('state', signal(resetValue));
    this.paymentMethodValidations.set('zipCode', signal(resetValue));
  }

  validatePaymentMethodField(key: string, value: string | any) {
    if (typeof value !== 'string')
      value = value.target.value;
    const entry = this.paymentMethodValidations.get(key);
    if (!entry) return;
    const data = entry();
    for (const validation of entry().validations) {
      switch (validation) {
        case 'required':
          data.error = !value.trim() ? 'This field is required' : ''
          break;
        case 'number':
          data.error = !(/^\d+$/.test(value)) ? 'Enter a valid security code' : ''
          break;
        case 'exactLength':
          const aux = value.toString();
          data.error = aux.length !== this.creditCardSecurityCodeMaxLen() ? `This field requires ${this.creditCardSecurityCodeMaxLen()} digits` : '';
          break;
        default:
          break;
      }
    }

    this.paymentMethodValidations.set(key, signal({ valid: !data.error, error: data.error, validations: data.validations, touched: true }));
  }


  updatePaymentMethod(newValue: any) {
    this.paymentMethod.update(value => ({ ...value, ...newValue }));
    this.out_paymentMethod.emit(this.paymentMethod());

    // Validate form fields:
    const key = Object.keys(newValue)[0];
    const value = newValue[key];
    this.validatePaymentMethodField(key, value);
  }

  billingAddressToggleChangeHandler(checked: boolean) {
    if (checked) {
      if (!this.states().length) {
        this.paymentMethodService.getStates().subscribe(states => this.states.set(states));
      }
      this.tearDownPaymentMethodValidations(['required']);
    } else {
      this.tearDownPaymentMethodValidations([])

      const updatedPaymentMethod = {
        ...this.paymentMethod(),
        address: undefined,
        city: undefined,
        state: undefined,
        zipCode: undefined,
        additionalAddress: undefined
      };
      this.paymentMethod.set(updatedPaymentMethod);
      this.out_paymentMethod.emit(this.paymentMethod());
    }
  }

  sendDataToANet() {

    this.isCheckDone.set(true);

    if (!this.isValidFormPaymentMethod())
      return this.#showValidationError();

    if (!this.isCheckedTermsAndConditions())
      return;

    const value = this.paymentMethod();
    const [month, year] = value.expirationDate.split('/');
    const ANetData: ANetPayload = {
      cardCode: value.cardCode,
      cardNumber: value.cardNumber,
      year,
      month
    };
    this.#authorizeService.sendPaymentDataToAnet(ANetData, (response: ANetResponse) => this.addPaymentMethod(response))
  }

  #showValidationError() {
    const keys: string[] = Array.from(this.paymentMethodValidations.keys());
    const data: any = this.paymentMethod();

    for (const key of keys) {
      const value = data[key];
      this.validatePaymentMethodField(key, value?.toString()?.trim() ?? '');
    }
  }

  addPaymentMethod(opaqueData: ANetResponse) {

    if (this.isWaitingResponse() || !this.isValidFormPaymentMethod() || !this.isCheckedTermsAndConditions())
      return;

    const payload = this.setUpPaymentMethodPayload(opaqueData);
    this.accountService
      .addPaymentMethod(payload, false)
      .pipe(
        tap(async () => {
          this.localStorageService.remove(LOCALSTORAGE_KEYS.SIGNUP_CURRENT_STEP);
          this.localStorageService.set(LOCALSTORAGE_KEYS.NEW_ACCOUNT, true);

          const sessionStored: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
          if (sessionStored)
            await this.firebaseCrudService.delete(FIREBASE_COLLECTIONS.SIGNUP_STEP, sessionStored.accountInfo.id.toString());
        }),
        finalize(async () => {
          await this.#paymentMethodSignupService
            .updateSubscription();
          this.out_continue.emit();
        })
      ).subscribe();
  }

  private setUpPaymentMethodPayload(opaqueData: any): PaymentDataPayload {
    const values = this.paymentMethod();
    const cardNumber = values.cardNumber.replaceAll(' ', '');
    const cardLastNumbers = cardNumber.substring(cardNumber.length - 4, cardNumber.length);
    if (this.isCheckedBillingAddress()) {
      this.billingAddress = {
        additional: values.additionalAddress || '',
        street: values.address || '',
        city: values.city || '',
        zip: values.zipCode || '',
        stateCode: values.state || ''
      }
    } else {
      this.billingAddress = {};
    }
    return {
      billingAddress: this.billingAddress,
      card: {
        firstName: values.firstName || '',
        lastName: values.lastName || '',
        expirationDate: values.expirationDate?.split('/').reverse().join('-') || '',
        number: cardLastNumbers,
      },
      opaqueData
    };
  }
}
